/* Tastenkürzel: Aktionsliste, Standardbelegung, Umbelegung (localStorage), Erkennung aus Tastaturereignissen und Anzeige.
 * Kürzel sind Zeichenketten wie 'Mod+Shift+G', 'Alt+H', 'F1' oder '['. 'Mod' ist Strg (Windows/Linux) oder Befehl (Mac).
 * Ohne DOM-Zugriff; navigator und localStorage werden nur geschützt gelesen. */
import I18n from './i18n.ts';
/** Aktion mit Name (übersetzt) und Standardbelegung */
export interface KeyAction { id: string; g: string; readonly n: string; def: string[] }
/** Was Keys von einem Tastaturereignis liest (KeyboardEvent passt) */
export interface KeyEvent { key: string; code?: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; shiftKey?: boolean; getModifierState?: (k: string) => boolean }

const Keys = (function () {
  'use strict';
  const _ = I18n.t;
  const STORE = 'gsme-keys-v1';

  /** [id, Gruppe, Name, Standardbelegung] */
  const DEF: [string, string, string, string[]][] = [
    ['new', 'Datei', 'keys.n.new', ['Alt+N']],
    ['open', 'Datei', 'keys.n.open', ['Mod+O']],
    ['export', 'Datei', 'keys.n.export', ['Mod+S']],
    ['settings', 'Datei', 'keys.n.settings', ['Mod+,']],
    ['help', 'Datei', 'keys.n.help', ['F1', '?']],

    ['undo', 'Bearbeiten', 'keys.n.undo', ['Mod+Z']],
    ['redo', 'Bearbeiten', 'keys.n.redo', ['Mod+Y', 'Mod+Shift+Z']],
    ['cut', 'Bearbeiten', 'keys.n.cut', ['Mod+X']],
    ['copy', 'Bearbeiten', 'keys.n.copy', ['Mod+C']],
    ['paste', 'Bearbeiten', 'keys.n.paste', ['Mod+V']],
    ['dup', 'Bearbeiten', 'keys.n.dup', ['Mod+D']],
    ['del', 'Bearbeiten', 'keys.n.del', ['Delete', 'Backspace']],
    ['selectAll', 'Bearbeiten', 'keys.n.selectAll', ['Mod+A']],
    ['find', 'Bearbeiten', 'keys.n.find', ['Mod+F']],
    ['rotate', 'Bearbeiten', 'keys.n.rotate', ['R']],
    ['rotateBack', 'Bearbeiten', 'keys.n.rotateBack', ['Shift+R']],
    ['cancel', 'Bearbeiten', 'keys.n.cancel', ['Escape']],
    ['confirm', 'Bearbeiten', 'keys.n.confirm', ['Enter']],

    ['group', 'Objekte', 'keys.n.group', ['Mod+G']],
    ['ungroup', 'Objekte', 'keys.n.ungroup', ['Mod+Shift+G', 'Mod+U']],
    ['carve', 'Objekte', 'keys.n.carve', ['Mod+Shift+C']],
    ['merge', 'Objekte', 'keys.n.merge', ['Mod+Shift+M']],
    ['hide', 'Objekte', 'keys.n.hide', ['H']],
    ['isolate', 'Objekte', 'keys.n.isolate', ['Alt+H']],
    ['showAll', 'Objekte', 'keys.n.showAll', ['Shift+H']],

    ['toolSelect', 'Werkzeuge', 'keys.n.toolSelect', ['V']],
    ['toolBox', 'Werkzeuge', 'keys.n.toolBox', ['B']],
    ['toolEntity', 'Werkzeuge', 'keys.n.toolEntity', ['E']],
    ['toolClip', 'Werkzeuge', 'keys.n.toolClip', ['X']],
    ['toolTexture', 'Werkzeuge', 'keys.n.toolTexture', ['M']],
    ['toolVertex', 'Werkzeuge', 'keys.n.toolVertex', ['C']],
    ['toolSculpt', 'Werkzeuge', 'keys.n.toolSculpt', ['U']],
    ['snap', 'Werkzeuge', 'keys.n.snap', ['G']],
    ['texLock', 'Werkzeuge', 'keys.n.texLock', ['T']],
    ['gridDown', 'Werkzeuge', 'keys.n.gridDown', ['[', ',']],
    ['gridUp', 'Werkzeuge', 'keys.n.gridUp', [']', '.']],

    ['view3d', 'Ansicht', 'keys.n.view3d', ['1']],
    ['viewTop', 'Ansicht', 'keys.n.viewTop', ['2']],
    ['viewFront', 'Ansicht', 'keys.n.viewFront', ['3']],
    ['viewSide', 'Ansicht', 'keys.n.viewSide', ['4']],
    ['viewQuad', 'Ansicht', 'keys.n.viewQuad', ['5']],
    ['fit', 'Ansicht', 'keys.n.fit', ['F']],
    ['fitAll', 'Ansicht', 'keys.n.fitAll', ['Shift+F']],
    ['aids', 'Ansicht', 'keys.n.aids', ['I']],
    ['light', 'Ansicht', 'keys.n.light', ['L']],
    ['fly', 'Ansicht', 'keys.n.fly', ['Z']],
    ['context', 'Ansicht', 'keys.n.context', ['Shift+F10', 'ContextMenu']]
  ];
  const GROUPS = ['Datei', 'Bearbeiten', 'Objekte', 'Werkzeuge', 'Ansicht'];

  /** Feste Bedienung, die sich nicht umbelegen lässt (nur zur Anzeige) */
  const FIXED: [string, number][] = [
    ['Auswahl', 0],
    ['Auswahl', 1],
    ['Auswahl', 2],
    ['Auswahl', 3],
    ['Auswahl', 4],
    ['Ansicht', 5],
    ['Ansicht', 6],
    ['Ansicht', 7],
    ['Flugmodus', 8],
    ['Punkte', 9],
    ['Dialoge', 10]
  ];

  /** Kürzel, die der Browser meist selbst behält und der Seite nicht überlässt */
  const RESERVED = /^(Mod\+(N|T|W|Q|L|R|Tab|[1-9])|Mod\+Shift\+(N|T|W|Q)|F5|F6|F11|F12|Alt\+F4)$/;

  let custom: Record<string, string[]> = load(), idx: Map<string, string> | null = null;
  const listeners: (() => void)[] = [];
  function load(): Record<string, string[]> {
    try {
      const s = JSON.parse(localStorage.getItem(STORE) as string), out: Record<string, string[]> = {};
      if (s && typeof s === 'object') Object.keys(s).forEach((id) => { if (Array.isArray(s[id]) && s[id].every((c: unknown) => typeof c === 'string')) out[id] = s[id]; });
      return out;
    } catch (e) { return {}; }
  }
  function save(): void { try { localStorage.setItem(STORE, JSON.stringify(custom)); } catch (e) { /* kein Speicher */ } }
  function touch(): void { idx = null; save(); listeners.forEach((f) => { try { f(); } catch (e) { /* ignorieren */ } }); }

  const GROUP_KEY: Record<string, string> = { Datei: 'file', Bearbeiten: 'edit', Objekte: 'objects', Werkzeuge: 'tools', Ansicht: 'view' };
  const groupName = (g: string): string => _('keys.group.' + (GROUP_KEY[g] || g));
  const BY = new Map<string, KeyAction>(DEF.map((d): [string, KeyAction] => [d[0], { id: d[0], g: d[1], get n() { return _(d[2]); }, def: d[3] }]));
  const list = DEF.map((d) => BY.get(d[0]) as KeyAction);

  function get(id: string): string[] { return custom[id] ? custom[id].slice() : (BY.has(id) ? (BY.get(id) as KeyAction).def.slice() : []); }
  function isCustom(id: string): boolean { return !!custom[id]; }
  function anyCustom(): boolean { return Object.keys(custom).length > 0; }
  function same(a: string[], b: string[]): boolean { return a.length === b.length && a.every((x, i) => x === b[i]); }
  function set(id: string, combos: string[]): void {
    if (!BY.has(id)) return;
    const c = combos.filter((x, i) => x && combos.indexOf(x) === i);
    if (same(c, (BY.get(id) as KeyAction).def)) delete custom[id]; else custom[id] = c;
    touch();
  }
  /** Kopie der eigenen Belegungen { id: [Kürzel] } (für die Synchronisation mit dem Backend) */
  function exportCustom(): Record<string, string[]> { const o: Record<string, string[]> = {}; Object.keys(custom).forEach((id) => { o[id] = custom[id].slice(); }); return o; }
  /** Eigene Belegungen ersetzen; unbekannte Aktionen und ungültige Einträge werden ignoriert */
  function importCustom(src: unknown): void {
    const out: Record<string, string[]> = {};
    if (src && typeof src === 'object') { const o = src as Record<string, unknown>; Object.keys(o).forEach((id) => { const v = o[id]; if (BY.has(id) && Array.isArray(v) && v.every((c) => typeof c === 'string')) out[id] = v.slice(); }); }
    custom = out; touch();
  }
  function reset(id: string): void { if (custom[id]) { delete custom[id]; touch(); } }
  function resetAll(): void { custom = {}; touch(); }

  function index(): Map<string, string> {
    if (idx) return idx;
    const m = new Map<string, string>();
    list.forEach((a) => get(a.id).forEach((c) => { if (!m.has(c)) m.set(c, a.id); }));
    idx = m;
    return m;
  }
  /** Welche Aktion belegt dieses Kürzel gerade? (exceptId wird übersprungen) */
  function owner(combo: string, exceptId?: string): string | null {
    for (const a of list) if (a.id !== exceptId && get(a.id).indexOf(combo) >= 0) return a.id;
    return null;
  }
  /** Belegt Platz `slot` (oder hängt an, wenn slot < 0 oder zu groß) mit `combo`. Nimmt es anderen Aktionen weg. Liefert deren Ids. */
  function assign(id: string, slot: number, combo: string): string[] {
    const stolen: string[] = [];
    list.forEach((a) => {
      if (a.id === id) return;
      const cur = get(a.id);
      if (cur.indexOf(combo) >= 0) { stolen.push(a.id); set(a.id, cur.filter((c) => c !== combo)); }
    });
    const mine = get(id);
    if (slot >= 0 && slot < mine.length) mine[slot] = combo; else mine.push(combo);
    set(id, mine);
    return stolen;
  }
  function removeSlot(id: string, slot: number): void { const c = get(id); c.splice(slot, 1); set(id, c); }
  function reserved(combo: string): boolean { return RESERVED.test(combo); }
  function onChange(fn: () => void): () => void { listeners.push(fn); return () => { const i = listeners.indexOf(fn); if (i >= 0) listeners.splice(i, 1); }; }

  /** Kürzel aus einem Tastaturereignis; null bei reinen Modifikatortasten */
  function fromEvent(e: KeyEvent): string | null {
    const k = e.key;
    if (!k || k === 'Control' || k === 'Shift' || k === 'Alt' || k === 'Meta' || k === 'AltGraph' || k === 'Dead' || k === 'Unidentified') return null;
    const altGr = !!(e.getModifierState && e.getModifierState('AltGraph'));
    const ctrl = !!(e.ctrlKey || e.metaKey) && !altGr;
    let alt = !!e.altKey && !altGr, shift = !!e.shiftKey, key: string;
    if (k === ' ') key = 'Space';
    else if (k.length === 1) {
      const m = e.code ? (/^Key([A-Z])$/.exec(e.code) || /^Digit([0-9])$/.exec(e.code)) : null;
      if (/[a-z]/i.test(k)) key = k.toUpperCase();
      else if (/[0-9]/.test(k)) key = k;
      else if (k.charCodeAt(0) > 127 && alt && m) key = m[1]; // Mac: Alt + Taste liefert Sonderzeichen
      else { key = k; shift = false; if (!ctrl) alt = false; } // Zeichen steckt Umschalt/AltGr/Alt schon im Zeichen
    } else key = k;
    return (ctrl ? 'Mod+' : '') + (alt ? 'Alt+' : '') + (shift ? 'Shift+' : '') + key;
  }
  /** Aktions-Id für ein Tastaturereignis oder null */
  function match(e: KeyEvent): string | null { const c = fromEvent(e); return c ? (index().get(c) || null) : null; }

  function split(c: string): { mods: string[]; key: string } {
    const mods: string[] = []; let rest = c;
    for (;;) { const m = /^(Mod|Alt|Shift)\+(.+)$/.exec(rest); if (!m) break; mods.push(m[1]); rest = m[2]; }
    return { mods: mods, key: rest };
  }
  const NAMES: Record<string, string> = { Delete: 'keys.k.Delete', Backspace: 'keys.k.Backspace', Escape: 'keys.k.Escape', Enter: 'keys.k.Enter', Space: 'keys.k.Space', ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓', PageUp: 'keys.k.PageUp', PageDown: 'keys.k.PageDown', ContextMenu: 'keys.k.ContextMenu', Tab: 'Tab' };
  const keyName = (k: string): string => (NAMES[k] ? (NAMES[k].indexOf('keys.') === 0 ? _(NAMES[k]) : NAMES[k]) : k);
  function isMac(): boolean {
    try { return /Mac|iPhone|iPad|iPod/.test((navigator.platform || '') + ' ' + (navigator.userAgent || '')); } catch (e) { return false; }
  }
  /** Anzeigetext: 'Strg+Umschalt+G' (Windows/Linux) oder '⇧⌘G' (Mac) */
  function fmt(combo: string, mac?: boolean): string {
    if (mac === undefined) mac = isMac();
    const s = split(combo), k = keyName(s.key);
    if (mac) return ['Alt', 'Shift', 'Mod'].filter((m) => s.mods.indexOf(m) >= 0).map((m) => ({ Mod: '⌘', Alt: '⌥', Shift: '⇧' }[m])).join('') + k;
    return s.mods.map((m) => ({ Mod: _('keys.mod.ctrl'), Alt: _('keys.mod.alt'), Shift: _('keys.mod.shift') }[m])).concat([k]).join('+');
  }
  function fmtAll(id: string, mac?: boolean): string { return get(id).map((c) => fmt(c, mac)).join('  ·  '); }

  return { list: list, exportCustom: exportCustom, importCustom: importCustom, groups: GROUPS, groupName: groupName, get fixed() { return FIXED.map((x) => [_('keys.fxg.' + x[0]), _('keys.fx.' + x[1] + '.c'), _('keys.fx.' + x[1] + '.d')]); }, get: get, set: set, reset: reset, resetAll: resetAll, isCustom: isCustom, anyCustom: anyCustom, owner: owner, assign: assign, removeSlot: removeSlot, reserved: reserved, onChange: onChange, fromEvent: fromEvent, match: match, split: split, fmt: fmt, fmtAll: fmtAll, isMac: isMac, byId: (id: string): KeyAction | undefined => BY.get(id) };
})();
export default Keys;
